import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useProfile } from '../../contexts/ProfileContext';
import logger from '../../utils/logger';

const SETTINGS_DOC_ID = 'systemConfig';

const defaultSettings = {
  // General Settings
  maintenanceMode: false,
  maintenanceMessage: 'The system is currently under maintenance. Please check back later.',

  // User Settings
  allowNewRegistrations: true,
  requireEmailVerification: false,
  maxFamilyMembers: 50,

  // Security Settings
  sessionTimeout: 60, // minutes
  maxLoginAttempts: 5,
  lockoutDuration: 15, // minutes

  // Notification Settings
  emailNotificationsEnabled: true,
  pushNotificationsEnabled: false,

  // Storage Settings
  maxFileSize: 100, // MB
  allowedFileTypes: 'images,documents,videos'
};

const SystemSettings = () => {
  const { profile } = useProfile();
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'systemSettings', SETTINGS_DOC_ID);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setSettings({ ...defaultSettings, ...docSnap.data() });
      } else {
        // Create default settings if they don't exist
        await setDoc(docRef, {
          ...defaultSettings,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setSettings(defaultSettings);
      }
    } catch (err) {
      logger.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const docRef = doc(db, 'systemSettings', SETTINGS_DOC_ID);
      await setDoc(docRef, {
        ...settings,
        updatedAt: serverTimestamp(),
        updatedBy: profile?.uid,
        updatedByName: profile?.displayName || profile?.email
      }, { merge: true });

      setSuccess('Settings saved successfully');
      setHasChanges(false);
    } catch (err) {
      logger.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleMaintenanceToggle = () => {
    if (!settings.maintenanceMode) {
      // Turning on maintenance mode - show confirmation
      setConfirmDialog({
        open: true,
        action: 'maintenance',
        title: 'Enable Maintenance Mode?',
        message: 'This will prevent all non-admin users from accessing the application. Are you sure?'
      });
    } else {
      handleChange('maintenanceMode', false);
    }
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === 'maintenance') {
      handleChange('maintenanceMode', true);
    }
    setConfirmDialog({ open: false, action: null });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            System Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure platform-wide settings and preferences
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadSettings}
            variant="outlined"
          >
            Reset
          </Button>
          <Button
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            variant="contained"
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Maintenance Mode */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: settings.maintenanceMode ? 'error.dark' : 'grey.800' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <SecurityIcon fontSize="large" color={settings.maintenanceMode ? 'error' : 'inherit'} />
                <Box flex={1}>
                  <Typography variant="h6">Maintenance Mode</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {settings.maintenanceMode
                      ? 'System is in maintenance mode - users cannot access the app'
                      : 'System is operating normally'}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.maintenanceMode}
                      onChange={handleMaintenanceToggle}
                      color="error"
                    />
                  }
                  label={settings.maintenanceMode ? 'ON' : 'OFF'}
                />
              </Box>
              {settings.maintenanceMode && (
                <TextField
                  fullWidth
                  label="Maintenance Message"
                  value={settings.maintenanceMessage}
                  onChange={(e) => handleChange('maintenanceMessage', e.target.value)}
                  multiline
                  rows={2}
                  sx={{ mt: 2 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* User Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon /> User Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <List>
                <ListItem>
                  <ListItemText
                    primary="Allow New Registrations"
                    secondary="Enable new users to create accounts"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.allowNewRegistrations}
                      onChange={(e) => handleChange('allowNewRegistrations', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Require Email Verification"
                    secondary="Users must verify email before accessing app"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.requireEmailVerification}
                      onChange={(e) => handleChange('requireEmailVerification', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Max Family Members"
                    secondary="Maximum members allowed per family"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      size="small"
                      value={settings.maxFamilyMembers}
                      onChange={(e) => handleChange('maxFamilyMembers', parseInt(e.target.value) || 50)}
                      sx={{ width: 80 }}
                      inputProps={{ min: 1, max: 500 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon /> Security Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <List>
                <ListItem>
                  <ListItemText
                    primary="Session Timeout"
                    secondary="Auto-logout after inactivity (minutes)"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      size="small"
                      value={settings.sessionTimeout}
                      onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value) || 60)}
                      sx={{ width: 80 }}
                      inputProps={{ min: 5, max: 480 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Max Login Attempts"
                    secondary="Failed attempts before lockout"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      size="small"
                      value={settings.maxLoginAttempts}
                      onChange={(e) => handleChange('maxLoginAttempts', parseInt(e.target.value) || 5)}
                      sx={{ width: 80 }}
                      inputProps={{ min: 1, max: 20 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Lockout Duration"
                    secondary="Account lockout time (minutes)"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      size="small"
                      value={settings.lockoutDuration}
                      onChange={(e) => handleChange('lockoutDuration', parseInt(e.target.value) || 15)}
                      sx={{ width: 80 }}
                      inputProps={{ min: 1, max: 120 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon /> Notification Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <List>
                <ListItem>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Send email notifications to users"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.emailNotificationsEnabled}
                      onChange={(e) => handleChange('emailNotificationsEnabled', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Push Notifications"
                    secondary="Send browser push notifications"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.pushNotificationsEnabled}
                      onChange={(e) => handleChange('pushNotificationsEnabled', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Storage Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorageIcon /> Storage Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <List>
                <ListItem>
                  <ListItemText
                    primary="Max File Size"
                    secondary="Maximum upload file size (MB)"
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      size="small"
                      value={settings.maxFileSize}
                      onChange={(e) => handleChange('maxFileSize', parseInt(e.target.value) || 100)}
                      sx={{ width: 80 }}
                      inputProps={{ min: 1, max: 500 }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText
                    primary="Allowed File Types"
                    secondary="Comma-separated list of allowed types"
                  />
                </ListItem>
              </List>
              <TextField
                fullWidth
                size="small"
                value={settings.allowedFileTypes}
                onChange={(e) => handleChange('allowedFileTypes', e.target.value)}
                helperText="e.g., images,documents,videos,audio"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemSettings;
